import { queryOptions, useQueries } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import { apiClient, AppError } from 'lib/api';
import queryKeys from './queryKeys';

export type RowStatus = 'ACTIVE' | 'PAUSED';

export interface CampaignEntry {
  id: string;
  name: string;
  status: RowStatus;
  budget: number;
  is_cbo: boolean;
}

const FOUR_HOURS = 1000 * 60 * 60 * 4;

export const campaignQueryOptions = (params: { facebookAccountId: string; campaignNameFilter?: string }) =>{
  const url = queryKeys.campaigns.join('/');

  return queryOptions({
    queryKey: [
      url,
      {
        facebookAccountId: params.facebookAccountId,
        campaignNameFilter: params.campaignNameFilter,
      },
    ],
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<CampaignEntry[]>(
        `/${url}/${params.facebookAccountId}`,
        { params: { campaign_name_filter: params.campaignNameFilter }, signal }
      );
      // Interceptor still returns errors instead of throwing — check and wrap
      if (isAxiosError(res)) {
        throw new AppError(res as AxiosError);
      }
      return res.data;
    },
    staleTime: FOUR_HOURS,
    gcTime: FOUR_HOURS,
    meta: { persist: true },
  });
}

interface UseCampaignsParams {
  facebookAccountId: string[] | undefined;
  campaignNameFilter?: string;
}

export const useCampaigns = ({ params, enabled }: { params: UseCampaignsParams; enabled?: boolean }) => {
  const { facebookAccountId = [], campaignNameFilter } = params;

  const queries = useQueries({
    queries: facebookAccountId.map((accountId) => ({
      ...campaignQueryOptions({ facebookAccountId: accountId, campaignNameFilter }),
      enabled: enabled ?? true,
    })),
  });

  const data = queries.flatMap((q) => q.data ?? []);
  const isPending = queries.some((q) => q.isPending);
  const isFetching = queries.some((q) => q.isFetching);
  const isFetched = queries.length > 0 && queries.every((q) => q.isFetched);
  const isError = queries.some((q) => q.isError);
  const errors = queries.filter((q) => q.isError).map((q) => q.error);
  const error = errors[0] ?? null;
  const dataUpdatedAt = queries.length > 0
    ? Math.min(...queries.map((q) => q.dataUpdatedAt))
    : 0;

  const refetch = async () => {
    await Promise.all(queries.map((q) => q.refetch()));
  };

  return { data, isPending, isFetching, isFetched, isError, error, errors, dataUpdatedAt, refetch };
};
